import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DialogDefaultV2 } from '../../../../components/v2';

const STATUS_ORDER = {
  EM_CRIACAO: 1,
  AGUARDANDO_INSPECAO: 2,
  EM_INSPECAO: 3,
  INSPECAO_FINALIZADA: 4,
  AGUARDANDO_DIGITACAO: 5,
  EM_DIGITACAO: 6,
  DIGITACAO_FINALIZADA: 7,
  EXECUTADA_GDIS: 8,
};

const SimilarityModal = ({ open, results, onCancel, onProceed, informationalOnly }) => {
  const sorted = [...results].sort((a, b) => {
    if ((b.duplicated ? 1 : 0) !== (a.duplicated ? 1 : 0)) {
      return (b.duplicated ? 1 : 0) - (a.duplicated ? 1 : 0);
    }
    if (b.similarityPercentage !== a.similarityPercentage) {
      return b.similarityPercentage - a.similarityPercentage;
    }
    return (STATUS_ORDER[a.status] || 99) - (STATUS_ORDER[b.status] || 99);
  });

  const hasDuplicates = sorted.some(r => r.duplicated);
  const title = hasDuplicates ? 'NSIRs Duplicadas Encontradas' : 'NSIRs Similares Encontradas';

  return (
    <DialogDefaultV2
      open={open}
      title={title}
      handlerCancel={onCancel}
      handlerConfirm={onProceed}
      cancelMessage="Cancelar"
      confirmMessage="Prosseguir"
      fullWidth
      maxWidth="xl"
      readOnly={informationalOnly}
    >
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: 14 }}>
        Foram encontradas NSIRs similares à que está sendo criada. Verifique as informações abaixo antes de continuar.
      </Typography>
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: 400,
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['NSIR', 'Equipamento', 'Tipo de Inspeção', 'Status', 'Inspetor', 'Similaridade', 'Razão', 'Link'].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    fontWeight: 600,
                    bgcolor: 'grey.50',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map(row => (
              <TableRow key={row.id} hover>
                <TableCell>{row.codigo}</TableCell>
                <TableCell>{row.equipamento}</TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{row.tipoServico}</Typography>
                  {row.subtipo && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      {row.subtipo}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{row.statusValue}</Typography>
                  {row.criadoPor && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      Criado por: {row.criadoPor}
                    </Typography>
                  )}
                  {row.criadoEm && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      Criado em: {row.criadoEm}
                    </Typography>
                  )}
                  {row.dataConclusao && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      Executada GDIS: {row.dataConclusao}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{row.inspetor}</TableCell>
                <TableCell>
                  {row.duplicated ? (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.5,
                        bgcolor: 'error.main',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >
                      DUPLICADA
                    </Box>
                  ) : (
                    <>
                      <Typography sx={{ fontWeight: 600 }}>{`${row.similarityPercentage}%`}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {`Equipamento: ${row.equipamentoSimilarity}%`}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {`Código: ${row.codigoSimilarity}%`}
                      </Typography>
                    </>
                  )}
                </TableCell>
                <TableCell>{row.reason}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Link
                    to={`/nsir/${row.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Abrir
                    </Typography>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </DialogDefaultV2>
  );
};

SimilarityModal.propTypes = {
  open: PropTypes.bool.isRequired,
  results: PropTypes.array.isRequired,
  onCancel: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  informationalOnly: PropTypes.bool,
};

SimilarityModal.defaultProps = {
  informationalOnly: false,
};

export default SimilarityModal;
